// Editor draft -> backend import-draft body.
//
// Forwards explicit nulls so clearing a field also clears the stored value.
// Unloaded line collections are omitted; loaded lines and known quantities
// retain their commercial and physical units without fabricated defaults.
import type { WorkflowInvoice, WorkflowInvoiceLine } from '../../types/workflowInvoice';

function isKnown(value: unknown): value is number {
  return typeof value === 'number' && Number.isFinite(value);
}

function counterparty(name: string | null | undefined, taxId: string | null | undefined, address: string | null | undefined) {

  return {
    name: name ?? null, taxId: taxId ?? null, address: address ?? null,
  };
}

function purchaseLinePayload(line: WorkflowInvoiceLine): Record<string, unknown> {
  return {
    productName: line.productName ?? null,
    quantityPcs: line.quantityPcs ?? null,
    quantityM2: line.quantityM2 ?? null,
    quantityM3: line.quantityM3 ?? null,
    conversionUnit: line.unit ?? line.conversionUnit ?? null,
    purchaseQuantity: line.commercialQuantity ?? null,
    purchaseUnit: line.commercialUnit ?? null,
    sourceSupplierOrderItemId: line.sourceOrderLineId ?? null,
    productVariantId: line.productVariantId ?? null,
    unitPrice: line.unitPrice ?? null,
    netAmount: line.netAmount ?? null,
    vatRate: line.taxRate ?? null,
    vatAmount: line.vatAmount ?? null,
    grossAmount: line.grossAmount ?? null,
    discountPercent: line.discountPercent ?? null,
    discountAmount: line.discountAmount ?? null,
    quantityRaw: line.quantityRaw ?? null,
    quantityUnitRaw: line.quantityUnitRaw ?? null,
  };
}

function salesLinePayload(line: WorkflowInvoiceLine): Record<string, unknown> {
  return {
    productName: line.productName ?? null,
    quantityPcs: line.quantityPcs ?? null,
    quantityM2: line.quantityM2 ?? null,
    quantityM3: line.quantityM3 ?? null,
    conversionUnit: line.unit ?? line.conversionUnit ?? null,
    saleQuantity: line.commercialQuantity ?? null,
    saleUnit: line.commercialUnit ?? null,
    sourceOrderProductId: line.sourceOrderLineId ?? null,
    productVariantId: line.productVariantId ?? null,
    unitPrice: line.unitPrice ?? null,
    netAmount: line.netAmount ?? null,
    vatRate: line.taxRate ?? null,
    vatAmount: line.vatAmount ?? null,
    grossAmount: line.grossAmount ?? null,
    discountPercent: line.discountPercent ?? null,
    discountAmount: line.discountAmount ?? null,
    quantityRaw: line.quantityRaw ?? null,
    quantityUnitRaw: line.quantityUnitRaw ?? null,
  };
}

/** Purchase draft body; unknown header fields stay null, never defaulted. */
export function buildPurchaseInvoicePayload(invoice: WorkflowInvoice): Record<string, unknown> {
  const seller = counterparty(invoice.sellerName, invoice.sellerTaxId, invoice.sellerAddress);
  const buyer = counterparty(invoice.buyerName, invoice.buyerTaxId, invoice.buyerAddress);
  return {
    documentNumber: invoice.originalDocumentNumber ?? null,
    documentKind: invoice.kind,
    currency: invoice.currency ?? null,
    exchangeRateDate: invoice.exchangeRateDate ?? null,
    exchangeRateEffectiveDate: invoice.exchangeRateSnapshot?.effectiveDate?.slice(0, 10) ?? null,
    supplierOrderId: invoice.supplierOrderId ?? null,
    supplierId: invoice.supplierId ?? null,
    correctsInvoiceId: invoice.correctsInvoiceId ?? null,
    issueDate: invoice.issueDate ?? null,
    dueDate: invoice.dueDate ?? null,
    exchangeRate: invoice.exchangeRateToPLN ?? null,
    subtotalNet: invoice.totalNet ?? null,
    vatTotal: invoice.totalVat ?? null,
    grossTotal: invoice.totalGross ?? null,
    orderReference: invoice.orderReference ?? null,
    paymentTerms: invoice.paymentTerms ?? null,
    documentNotes: invoice.documentNotes ?? null,
    ...(seller ? { seller } : {}),
    ...(buyer ? { buyer } : {}),
    ...(invoice.linesLoaded ? { lines: invoice.lines.map(purchaseLinePayload) } : {}),
  };
}

/**
 * Sales draft body. `documentKind` is authoritative for imported documents;
 * compatible kinds also carry the legacy `type` for existing readers.
 */
export function buildSalesInvoicePayload(invoice: WorkflowInvoice): Record<string, unknown> {
  const seller = counterparty(invoice.sellerName, invoice.sellerTaxId, invoice.sellerAddress);
  const buyer = counterparty(invoice.buyerName, invoice.buyerTaxId, invoice.buyerAddress);
  const type = invoice.kind === 'STANDARD'
    ? 'FINAL'
    : invoice.kind === 'PROFORMA' || invoice.kind === 'ADVANCE'
      ? invoice.kind
      : undefined;
  return {
    documentNumber: invoice.originalDocumentNumber ?? null,
    ...(type ? { type } : {}),
    documentKind: invoice.kind,
    subtotalNet: invoice.totalNet ?? null,
    vatTotal: invoice.totalVat ?? null,
    exchangeRate: invoice.exchangeRateToPLN ?? null,
    exchangeRateDate: invoice.exchangeRateDate ?? null,
    exchangeRateEffectiveDate: invoice.exchangeRateSnapshot?.effectiveDate?.slice(0, 10) ?? null,
    currency: invoice.currency ?? null,
    orderId: invoice.orderId ?? null,
    issueDate: invoice.issueDate ?? null,
    dueDate: invoice.dueDate ?? null,
    totalAmount: invoice.totalGross ?? null,
    orderReference: invoice.orderReference ?? null,
    paymentTerms: invoice.paymentTerms ?? null,
    documentNotes: invoice.documentNotes ?? null,
    ...(seller ? { seller } : {}),
    ...(buyer ? { buyer } : {}),
    ...(invoice.linesLoaded ? { lines: invoice.lines.map(salesLinePayload) } : {}),
  };
}

export { isKnown };
